<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSupportAlerts } from '@/composables/useSupportAlerts'

/**
 * The panel's frame: a sidebar built from the signed-in admin's permissions, and the page.
 *
 * The menu is filtered by permission rather than by role, so adding a permission to a role changes
 * what that person sees without a deploy. The server still decides — every `/admin/v1` call checks
 * again — this only avoids showing a door that will not open.
 */
const auth = useAuthStore()
const supportAlerts = useSupportAlerts(() => auth.can('support.read'))
const route = useRoute()
const router = useRouter()

const collapsed = ref(false)

interface MenuLink {
  label: string
  icon: string
  to: string
  permission: string | null
  badge?: 'support'
}

/**
 * Forty-one destinations, in nine groups.
 *
 * Flat, they made a single column taller than any screen: reaching «امنیت حساب» meant scrolling
 * past every report, and because the *page* scrolled rather than the menu, the header and the
 * content went with it. Grouping is not decoration here — it is what makes the last entry
 * reachable without the first leaving the screen.
 *
 * Groups follow the job rather than the URL. «فروش» holds orders, payments, shipping and the
 * warehouse lane because one person does all four in a morning; the reports sit together because
 * reading them is a different afternoon from doing any of it.
 */
type MenuEntry =
  | { kind: 'link', label: string, icon: string, to: string, permission: string | null }
  | { kind: 'group', label: string, icon: string, links: MenuLink[] }

const MENU: MenuEntry[] = [
  { kind: 'link', label: 'داشبورد', icon: '◫', to: '/dashboard', permission: null },

  {
    kind: 'group',
    label: 'فروش',
    icon: '✦',
    links: [
      { label: 'سفارش‌ها', icon: '✦', to: '/orders', permission: 'orders.read' },
      { label: 'پرداخت‌ها', icon: '₪', to: '/payments', permission: 'payments.read' },
      { label: 'ارسال', icon: '▣', to: '/shipping', permission: 'shipping.read' },
      // The phone lane (G6.13). Same permission as the order list it shows — the screens gate
      // dispatch themselves, so a read-only operator still gets a usable picking list.
      { label: 'حالت انبار', icon: '▥', to: '/m/orders', permission: 'orders.read' },
    ],
  },

  {
    kind: 'group',
    label: 'کاتالوگ',
    icon: '◇',
    links: [
      { label: 'محصولات', icon: '◇', to: '/products', permission: 'catalog.products.read' },
      { label: 'روتین‌ها', icon: '❖', to: '/catalog/bundles', permission: 'catalog.products.read' },
      { label: 'دسته و برند', icon: '❖', to: '/taxonomy', permission: 'catalog.products.read' },
      // Editing-only: the API refuses even the listing to a read-only account, so a link that led
      // to a permanent 403 would be worse than no link.
      { label: 'نشان‌ها', icon: '❂', to: '/badges', permission: 'catalog.products.write' },
      { label: 'قفسه‌ها', icon: '▤', to: '/collections', permission: 'catalog.products.write' },
      { label: 'استوری‌ها', icon: '◎', to: '/stories', permission: 'catalog.products.write' },
      { label: 'تقاضای در انتظار', icon: '⧗', to: '/catalog/demand', permission: 'catalog.products.read' },
    ],
  },

  {
    kind: 'group',
    label: 'بازخورد مشتری',
    icon: '★',
    links: [
      // In-house support messaging (spec 2026-10-05); the badge counts conversations waiting on the team.
      { label: 'پشتیبانی', icon: '✉', to: '/support', permission: 'support.read', badge: 'support' },
      { label: 'پشتیبانی روی گوشی', icon: '☏', to: '/m/support', permission: 'support.read' },
      { label: 'دیدگاه‌ها', icon: '★', to: '/reviews', permission: 'catalog.products.read' },
      /*
       * The other half of the same queue (G6.3): one screen with two tabs, entered by whichever
       * half brought somebody here. The path is `/moderation/questions` rather than a query
       * string because the title below matches on the path.
       */
      { label: 'پرسش و پاسخ', icon: '؟', to: '/moderation/questions', permission: 'catalog.products.read' },
    ],
  },

  {
    kind: 'group',
    label: 'محتوا',
    icon: '✎',
    links: [
      { label: 'صفحه‌ساز', icon: '▦', to: '/content/builder', permission: 'content.builder.write' },
      // Beside the builder, because it is the same object and the same right (G6.14).
      { label: 'آزمایش A/B', icon: '⚖', to: '/experiments', permission: 'content.builder.write' },
      { label: 'مقاله‌ها', icon: '✒', to: '/content/articles', permission: 'content.articles.write' },
      { label: 'نویسندگان', icon: '☺', to: '/content/authors', permission: 'content.articles.write' },
      { label: 'پرونده‌ها', icon: '❑', to: '/content/series', permission: 'content.articles.write' },
      { label: 'محتوا', icon: '✎', to: '/content', permission: 'content.articles.write' },
    ],
  },

  {
    kind: 'group',
    label: 'سئو و جست‌وجو',
    icon: '⌾',
    links: [
      { label: 'چیدمان جست‌وجو', icon: '⌗', to: '/search-merchandising', permission: 'search.manage' },
      /*
       * The SEO rights sit on `content_editor` rather than on the catalogue roles, so a
       * merchandiser sees the shelf entries above and none of these.
       */
      { label: 'سئوی صفحه‌ها', icon: '⌾', to: '/seo/system-pages', permission: 'seo.system_pages.edit' },
      { label: 'صفحه‌های فرود', icon: '⟡', to: '/seo/landing-pages', permission: 'seo.landing_pages.manage' },
      // Carries `seo.system_pages.edit` — the right its own API takes — rather than a landing-page
      // right, so it never appears to a role that would get a 403 on its first request.
      { label: 'فیدهای محصول', icon: '⇲', to: '/seo/feeds', permission: 'seo.system_pages.edit' },
    ],
  },

  {
    kind: 'group',
    label: 'بازاریابی',
    icon: '✉',
    links: [
      // Read, not write: finance may look at what the codes cost without being able to mint one.
      { label: 'کدهای تخفیف', icon: '✂', to: '/coupons', permission: 'commerce.coupons.read' },
      { label: 'کمپین‌ها', icon: '⏱', to: '/campaigns', permission: 'commerce.coupons.read' },
      { label: 'کمپین‌های بازاریابی', icon: '◎', to: '/marketing-campaigns', permission: 'marketing.campaigns.read' },
      { label: 'بخش‌های مشتری', icon: '◍', to: '/segments', permission: 'marketing.segments.read' },
      { label: 'پیام گروهی', icon: '✉', to: '/broadcasts', permission: 'marketing.broadcasts.write' },
    ],
  },

  {
    kind: 'group',
    label: 'مشتریان',
    icon: '◌',
    links: [
      { label: 'مشتریان', icon: '◌', to: '/customers', permission: 'customers.read' },
      { label: 'جورنی‌ها', icon: '◈', to: '/journeys', permission: 'journeys.analytics.read' },
      { label: 'مشاوره‌ها', icon: '✧', to: '/consults', permission: 'journeys.analytics.read' },
    ],
  },

  {
    kind: 'group',
    label: 'گزارش‌ها',
    icon: '⊞',
    links: [
      /*
       * The report screens (G1.3.6), each with its own permission, so the filter below does the
       * work the reports API also does server-side: an analyst never sees «ارزش افزوده» or «سود
       * ناخالص», and a label that would itself reveal the store tracks margin never renders.
       */
      { label: 'حسابداری و فروش', icon: '⊞', to: '/reports/accounting', permission: 'reports.view' },
      { label: 'تسویه درگاه‌ها', icon: '⇄', to: '/reports/settlements', permission: 'reports.view' },
      { label: 'ارزش افزوده', icon: '％', to: '/reports/vat', permission: 'reports.finance' },
      { label: 'سود ناخالص', icon: '◭', to: '/reports/margin', permission: 'reports.finance' },
      { label: 'درآمد مسیرها', icon: '⌁', to: '/reports/journeys-revenue', permission: 'reports.view' },
      { label: 'پربازدیدها', icon: '◉', to: '/reports/views', permission: 'reports.view' },
      { label: 'تحلیل جست‌وجو', icon: '⌕', to: '/reports/search', permission: 'reports.view' },
      { label: 'بازیابی سبد رهاشده', icon: '↩', to: '/reports/cart-recovery', permission: 'reports.view' },
    ],
  },

  {
    kind: 'group',
    label: 'سیستم',
    icon: '⚙',
    links: [
      { label: 'تنظیمات', icon: '⚙', to: '/settings', permission: 'settings.read' },
      { label: 'کاربران پنل', icon: '☰', to: '/admins', permission: 'admins.read' },
      { label: 'گزارش فعالیت', icon: '≡', to: '/activity', permission: 'ops.logs.read' },
      { label: 'امنیت حساب', icon: '⚿', to: '/security', permission: null },
    ],
  },
]

function allowed(permission: string | null): boolean {
  return permission === null || auth.can(permission)
}

/**
 * The menu this admin may see, with any group that ends up empty removed entirely.
 *
 * A group header that opens onto nothing is worse than a missing group: it tells the operator the
 * panel has a «گزارش‌ها» section and then shows none of it, which reads as a fault rather than as
 * a permission they do not hold.
 */
const menu = computed<MenuEntry[]>(() =>
  MENU.flatMap((entry): MenuEntry[] => {
    if (entry.kind === 'link') return allowed(entry.permission) ? [entry] : []

    const links = entry.links.filter((link) => allowed(link.permission))

    return links.length > 0 ? [{ ...entry, links }] : []
  }),
)

/** Every destination, flattened — for the page title and for deciding which group is open. */
const destinations = computed<MenuLink[]>(() =>
  menu.value.flatMap((entry) => (entry.kind === 'link' ? [entry] : entry.links)),
)

function matches(to: string): boolean {
  return route.path === to || route.path.startsWith(to + '/')
}

/**
 * The title, by longest matching prefix rather than by first match.
 *
 * The flat menu relied on ordering — `/content` had to be listed after `/content/builder` or it
 * would claim the builder's pages and title them «محتوا» — a constraint nobody editing a list of
 * forty links can be expected to hold in mind. Longest-prefix removes it: `/content/builder` wins
 * over `/content` because it is more specific, wherever either happens to sit.
 */
const current = computed(() => {
  const best = destinations.value
    .filter((link) => matches(link.to))
    .sort((a, b) => b.to.length - a.to.length)[0]

  return best?.label ?? 'اوشلا'
})

/** The group holding the current route, so it is already open when the panel loads on that page. */
const activeGroup = computed(() => {
  const found = menu.value.find(
    (entry) => entry.kind === 'group' && entry.links.some((link) => matches(link.to)),
  )

  return found?.label ?? null
})

/*
 * Groups the operator has opened or shut by hand, overriding the active-group default.
 *
 * An override map rather than a list of open groups, so that «shut» is expressible: a plain list
 * cannot tell «never touched» from «deliberately closed», and the group holding the current page
 * would spring open again on every navigation inside it.
 */
const toggled = ref<Record<string, boolean>>({})

function isOpen(label: string): boolean {
  return toggled.value[label] ?? label === activeGroup.value
}

function toggle(label: string): void {
  toggled.value = { ...toggled.value, [label]: !isOpen(label) }
}

async function signOut(): Promise<void> {
  await auth.logout()
  await router.push({ name: 'login' })
}
</script>

<template>
  <div class="admin-shell" :class="{ 'is-collapsed': collapsed }">
    <aside class="admin-sidebar">
      <div class="admin-brand">
        <img class="admin-seal" src="/brand/oshela-icon.svg" alt="" width="34" height="34">
        <div v-if="!collapsed">
          <b>اوشلا</b>
          <small>پنل مدیریت</small>
        </div>
      </div>

      <nav aria-label="فهرست پنل">
        <template v-for="entry in menu" :key="entry.label">
          <RouterLink v-if="entry.kind === 'link'" :to="entry.to" class="admin-link">
            <span class="admin-glyph" aria-hidden="true">{{ entry.icon }}</span>
            <b v-if="!collapsed">{{ entry.label }}</b>
          </RouterLink>

          <div v-else class="admin-group" :class="{ 'is-open': isOpen(entry.label) }">
            <!--
              A button, not a link: it opens a section rather than going anywhere, and a screen
              reader that announces it as a link would promise a page that does not exist.
            -->
            <button
              type="button"
              class="admin-group-head"
              :aria-expanded="isOpen(entry.label)"
              :title="collapsed ? entry.label : undefined"
              @click="toggle(entry.label)"
            >
              <span class="admin-glyph" aria-hidden="true">{{ entry.icon }}</span>
              <b v-if="!collapsed">{{ entry.label }}</b>
              <span v-if="!collapsed" class="admin-caret" aria-hidden="true">⌄</span>
            </button>

            <div v-show="isOpen(entry.label)" class="admin-sub">
              <RouterLink
                v-for="link in entry.links"
                :key="link.to"
                :to="link.to"
                class="admin-link is-sub"
                :title="collapsed ? link.label : undefined"
              >
                <span class="admin-glyph" aria-hidden="true">{{ link.icon }}</span>
                <b v-if="!collapsed">{{ link.label }}</b>
                <span v-if="link.badge === 'support' && supportAlerts.unanswered.value > 0" class="admin-badge">{{ supportAlerts.unanswered.value }}</span>
              </RouterLink>
            </div>
          </div>
        </template>
      </nav>

      <button class="admin-collapse" type="button" @click="collapsed = !collapsed">
        {{ collapsed ? '»' : '« بستن منو' }}
      </button>
    </aside>

    <div class="admin-main">
      <header class="admin-header">
        <h1>{{ current }}</h1>

        <div class="admin-user">
          <div>
            <b>{{ auth.admin?.name }}</b>
            <small>{{ auth.admin?.roles.join('، ') }}</small>
          </div>
          <button type="button" @click="signOut">خروج</button>
        </div>
      </header>

      <main class="admin-content">
        <RouterLink
          v-if="supportAlerts.notice.value"
          to="/support"
          class="admin-support-notice"
          role="status"
          @click="supportAlerts.dismiss()"
        >
          ✉ {{ supportAlerts.notice.value }} — باز کردن صندوق
        </RouterLink>
        <RouterView />
      </main>
    </div>
  </div>
</template>

<style scoped>
.admin-badge { margin-inline-start: auto; min-inline-size: 20px; padding: 1px 6px; border-radius: 999px; background: #e8457a; color: #fff; font-size: 11px; font-weight: 700; text-align: center; }
.admin-support-notice { display: block; margin: 0 0 12px; padding: 10px 14px; border-radius: 12px; background: #ffe3ec; color: #a3234f; font-weight: 700; text-decoration: none; }
/*
 * The shell owns the viewport and neither column grows past it.
 *
 * It used to be `min-block-size: 100vh`, which let the *document* scroll: a menu of forty links
 * made the page taller than the screen, so reaching the last one scrolled the header and the
 * content away with it. Now the shell is exactly one viewport and each column scrolls inside
 * itself, which is what «only the sidebar should move» actually requires.
 */
.admin-shell {
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  block-size: 100vh;
  overflow: hidden;
  background: var(--cream, #fff7f9);
}

.admin-shell.is-collapsed {
  grid-template-columns: 72px minmax(0, 1fr);
}

.admin-sidebar {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px 12px;
  background: var(--plum-deep, #3d0e22);
  color: white;
  /*
   * `overscroll-behavior: contain` is the half that people forget. Without it, scrolling to the
   * end of the menu hands the remaining momentum to the page behind — the content jumps while the
   * pointer is over the sidebar, which is exactly the complaint this fixes.
   */
  block-size: 100vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgb(255 255 255 / 22%) transparent;
}

.admin-sidebar::-webkit-scrollbar {
  inline-size: 8px;
}

.admin-sidebar::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: rgb(255 255 255 / 18%);
}

.admin-sidebar::-webkit-scrollbar-thumb:hover {
  background: rgb(255 255 255 / 30%);
}

/* The brand stays put while the list moves under it, so there is always something to aim at. */
.admin-brand {
  position: sticky;
  inset-block-start: -20px;
  z-index: 1;
  margin-block-start: -20px;
  padding-block-start: 20px;
  background: var(--plum-deep, #3d0e22);
}

.admin-brand {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 4px 8px 20px;
}

.admin-seal {
  display: block;
  inline-size: 34px;
  block-size: 34px;
  flex-shrink: 0;
}

.admin-brand b {
  display: block;
  font-size: 14px;
}

.admin-brand small {
  font-size: 9px;
  letter-spacing: 1.5px;
  color: rgb(255 255 255 / 55%);
}

.admin-sidebar nav {
  display: grid;
  gap: 2px;
  align-content: start;
  flex: 1;
}

.admin-group-head {
  display: flex;
  gap: 12px;
  align-items: center;
  inline-size: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: rgb(255 255 255 / 62%);
  font: inherit;
  font-size: 12px;
  letter-spacing: 0.2px;
  text-align: start;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.admin-group-head:hover {
  background: rgb(255 255 255 / 6%);
  color: white;
}

.admin-group-head b {
  flex: 1;
  font-weight: 600;
}

.admin-caret {
  font-size: 11px;
  opacity: 0.55;
  transition: transform 0.18s;
}

.admin-group.is-open .admin-caret {
  transform: rotate(180deg);
}

/*
 * The rail is what makes a child look like a child at a glance. In an RTL panel it belongs on the
 * inline-start edge, which `border-inline-start` places correctly without a direction check.
 */
.admin-sub {
  display: grid;
  gap: 2px;
  margin-inline-start: 21px;
  padding-inline-start: 8px;
  border-inline-start: 1px solid rgb(255 255 255 / 12%);
}

.admin-shell.is-collapsed .admin-sub {
  margin-inline-start: 0;
  padding-inline-start: 0;
  border-inline-start: 0;
}

.admin-link.is-sub {
  padding-block: 8px;
  font-size: 12.5px;
}

.admin-glyph {
  inline-size: 18px;
  flex-shrink: 0;
  text-align: center;
}

.admin-link {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 11px 12px;
  border-radius: 11px;
  color: rgb(255 255 255 / 72%);
  font-size: 13px;
  text-decoration: none;
  transition: background 0.15s;
}

.admin-link:hover {
  background: rgb(255 255 255 / 8%);
  color: white;
}

.admin-link.router-link-active {
  background: rgb(255 255 255 / 14%);
  color: white;
}

.admin-collapse {
  padding: 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: rgb(255 255 255 / 50%);
  font: inherit;
  font-size: 11px;
  cursor: pointer;
}

.admin-main {
  display: flex;
  flex-direction: column;
  min-inline-size: 0;
  /* Lets the flex child above shrink so its own overflow works rather than growing the column. */
  min-block-size: 0;
  block-size: 100vh;
}

.admin-header {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  padding: 18px 28px;
  border-block-end: 1px solid var(--line, rgb(110 23 54 / 14%));
  background: var(--ivory, #fffefe);
}

.admin-header h1 {
  margin: 0;
  font-size: 17px;
  color: var(--plum-deep, #3d0e22);
}

.admin-user {
  display: flex;
  gap: 14px;
  align-items: center;
}

.admin-user b {
  display: block;
  font-size: 13px;
  color: var(--plum-deep, #3d0e22);
}

.admin-user small {
  font-size: 11px;
  color: var(--muted, #7a6870);
}

.admin-user button {
  padding: 7px 14px;
  border: 1px solid var(--line, rgb(110 23 54 / 14%));
  border-radius: 999px;
  background: transparent;
  color: var(--plum, #cc3366);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

/*
 * The content scrolls itself, because the shell no longer lets the document scroll. Without this
 * a page taller than the viewport would simply be cut off with no way to reach the rest — the
 * cost of clipping the shell, paid here.
 *
 * `overscroll-behavior` again, for the same reason as the sidebar: reaching the end of a long
 * table should stop, not hand the scroll to whatever is behind it.
 */
.admin-content {
  flex: 1;
  min-block-size: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px 28px 48px;
}

@media (width <= 800px) {
  .admin-shell,
  .admin-shell.is-collapsed {
    grid-template-columns: 64px minmax(0, 1fr);
  }
}
</style>
